import "toastify-js/src/toastify.css";
import Toastify from "toastify-js";

import {
  BattingCagePreviewTriggersCookie,
  cloneDeep,
  getURLSearchParams,
  isNumber,
  removeItemFromArrayByIndex,
  store,
  updateItemInArrayAtIndex,
  uuid,
} from "../shared";
import { ITrigger } from "../types";

const BaseToastStyles = {
  letterSpacing: "0.14px",
  borderRadius: "6px",
  boxShadow: "0px 3px 6px #7176813d",
  color: "#000000",
};

const InfoToastStyles = {
  background: "#d5e0ed",
};

const ErrorToastStyles = {
  background: "#ffe6e6",
};

export const TriggerPreviewsModule = (() => {
  const showPreviewToast = () => {
    const toast = Toastify({
      text: "This window is in preview mode",
      duration: 5000,
      gravity: "bottom",
      position: "center",
      stopOnFocus: true,
      style: {
        ...BaseToastStyles,
        ...InfoToastStyles,
      },
      onClick: function () {
        toast.hideToast();
      },
    });

    toast.showToast();
  };

  const showPreviewWarningToast = () => {
    const toast = Toastify({
      text: `A change in preview settings have been detected and the page will not work as expected. Please open a new tab to continue preview mode.`,
      duration: -1,
      close: true,
      gravity: "bottom",
      position: "center",
      stopOnFocus: true,
      style: {
        ...BaseToastStyles,
        ...ErrorToastStyles,
      },
    });

    toast.showToast();
  };

  const updateBattingCageScriptPreviewCookiePersistedExperience = (
    trigger: ITrigger
  ) => {
    const battingCagePreviewTriggersCookie =
      BattingCagePreviewTriggersCookie.get();

    const updatedBattingCagePreviewTriggersCookie = cloneDeep(
      battingCagePreviewTriggersCookie
    );

    const windowNameUUID = window.name;

    if (!windowNameUUID) return;

    const currentWindowPreviewIndex =
      updatedBattingCagePreviewTriggersCookie.findIndex(
        ({ name }) => windowNameUUID === name
      );

    const currentWindowPreviewData =
      updatedBattingCagePreviewTriggersCookie[currentWindowPreviewIndex];

    if (!currentWindowPreviewData) return;

    const { persistedExperience } = currentWindowPreviewData;

    const updatedPersistedExperience = [...(persistedExperience || [])];

    const newPersistedTrigger = {
      triggerId: trigger.triggerId,
      behaviorId: trigger.behaviorId,
      behaviorType: trigger.behaviorType,
      pageUrl: "",
      timestamp: new Date().toISOString(),
      utcTimestamp: new Date().toUTCString(),
    };

    updatedPersistedExperience.push(newPersistedTrigger);

    updateItemInArrayAtIndex(
      updatedBattingCagePreviewTriggersCookie,
      currentWindowPreviewIndex,
      {
        ...currentWindowPreviewData,
        persistedExperience: updatedPersistedExperience,
      }
    );

    store.set("persistedExperience", updatedPersistedExperience);

    BattingCagePreviewTriggersCookie.set({
      value: updatedBattingCagePreviewTriggersCookie,
    });
  };

  const createPreviewSessionInBattingCagePreviewCookie = () => {
    const battingCagePreviewTriggersCookie =
      BattingCagePreviewTriggersCookie.get();

    const currentPreviewSessionName = window.name || "";

    // Query params are appended to window name after second dash
    const currentWindowBattingCagePreviewQueryParams =
      currentPreviewSessionName.split("--")[1];

    const originalUrlParams = window.location.search;

    const windowNameUUID = `batting-cage-preview--${originalUrlParams}--${uuid()}`;

    if (
      currentWindowBattingCagePreviewQueryParams &&
      currentWindowBattingCagePreviewQueryParams !== originalUrlParams
    ) {
      const updateBattingCagePreviewTriggersCookie = cloneDeep(
        battingCagePreviewTriggersCookie
      );

      const previewSessionIndexToRemove =
        battingCagePreviewTriggersCookie.findIndex(
          ({ name }) => name === currentWindowBattingCagePreviewQueryParams
        );

      removeItemFromArrayByIndex(
        updateBattingCagePreviewTriggersCookie,
        previewSessionIndexToRemove
      );

      BattingCagePreviewTriggersCookie.set({
        value: updateBattingCagePreviewTriggersCookie,
      });

      showPreviewWarningToast();
      return;
    }

    const newPreviewSession = {
      name: windowNameUUID,
      originalUrlParams,
      isActiveTab: true,
      scriptTracked: true,
      persistedExperience: null,
    };

    const updatedBattingCagePreviewTriggersCookie =
      battingCagePreviewTriggersCookie.concat(newPreviewSession);

    BattingCagePreviewTriggersCookie.set({
      value: updatedBattingCagePreviewTriggersCookie,
    });

    window.name = windowNameUUID;

    showPreviewToast();
  };

  const updatePreviewSessionActiveTab = ({
    isPageHidden,
  }: {
    isPageHidden: boolean;
  }) => {
    const battingCagePreviewTriggersCookie =
      BattingCagePreviewTriggersCookie.get();

    if (!battingCagePreviewTriggersCookie.length) return;

    const previewSessionName = window.name;

    const updateBattingCagePreviewTriggersCookie =
      battingCagePreviewTriggersCookie.map((previewSession) => {
        const isCurrentTabActive = !isPageHidden;
        return {
          ...previewSession,
          isActiveTab:
            previewSession?.name === previewSessionName
              ? isCurrentTabActive
              : false,
        };
      });

    BattingCagePreviewTriggersCookie.set({
      value: updateBattingCagePreviewTriggersCookie,
    });
  };

  const updateBattingCageScriptPreviewCookieOnPageVisibilityChange = (
    isPageHidden: boolean
  ) => {
    if (!store.get("isPreviewMode")) return;

    updatePreviewSessionActiveTab({ isPageHidden });

    if (!isPageHidden) {
      showPreviewToast();
    }
  };

  const getPersistedExperienceFromBattingCagePreviewCookie = () => {
    const isPreviewMode = store.get("isPreviewMode");

    if (!isPreviewMode) return;

    const previewSessionName = window.name;

    const battingCagePreviewTriggersCookie =
      BattingCagePreviewTriggersCookie.get();

    const previewSession = battingCagePreviewTriggersCookie.find(
      (allPreviewSessions) => allPreviewSessions?.name === previewSessionName
    );

    if (!previewSession) return;

    return previewSession.persistedExperience;
  };

  const start = () => {
    /*
      Documentation
      https://centerfieldmedia.atlassian.net/wiki/spaces/CFB/pages/719028225/API+Integration+-+Multipage#Content-Previews
    */
    const previewClusterVersionId = getURLSearchParams(
      "preview_cluster_version"
    )?.[0];
    const previewExperimentVariationId = getURLSearchParams(
      "preview_experiment_variation"
    )?.[0];

    const previewForcedTriggeredContent = window.location.search.includes(
      "preview_activated_trigger"
    );

    const previewDomain = getURLSearchParams("preview_domain")?.[0];
    // Backward compatibility
    const previewLandingPage = getURLSearchParams("preview_landing_page")?.[0];

    const isPreviewingDomain =
      previewDomain === "true" ||
      previewLandingPage === "true" ||
      previewForcedTriggeredContent;

    const isPreviewMode =
      isNumber(previewClusterVersionId) ||
      isNumber(previewExperimentVariationId);

    const isPreviewZonesMode =
      getURLSearchParams("preview_zones")?.[0] === "contentonly";

    const windowName = window.name || "";

    /**
     * Continued Session on same window but O&O site deleted url params
     */
    if (windowName && (!isPreviewingDomain || !isPreviewMode)) {
      const isContinuedPreviewSession =
        windowName.includes("preview_cluster_version") ||
        windowName.includes("preview_experiment_variation") ||
        windowName.includes("preview_activated_trigger") ||
        windowName.includes("preview_domain") ||
        windowName.includes("preview_landing_page");

      const isPreviewingDomain =
        windowName.includes("preview_activated_trigger") ||
        windowName.includes("preview_domain") ||
        windowName.includes("preview_landing_page");

      if (isContinuedPreviewSession) {
        // Query params are appended to window name after second dash
        const currentWindowBattingCagePreviewQueryParams =
          windowName.split("--")[1];

        const searchParams = new URLSearchParams(
          currentWindowBattingCagePreviewQueryParams
        );

        store.set("isPreviewMode", true);

        store.set("previewModeParameters", {
          previewClusterVersionId: searchParams.get("preview_cluster_version"),
          previewExperimentVariationId: searchParams.get(
            "preview_experiment_variation"
          ),
        });

        updatePreviewSessionActiveTab({ isPageHidden: false });
      }

      return { isPreviewingDomain };
    }

    if (isPreviewMode) {
      store.set("isPreviewMode", true);
      store.set("isPreviewZonesMode", isPreviewZonesMode);

      store.set("previewModeParameters", {
        previewClusterVersionId,
        previewExperimentVariationId,
      });
      createPreviewSessionInBattingCagePreviewCookie();
    }

    return {
      isPreviewingDomain,
    };
  };

  return {
    start,
    updateBattingCageScriptPreviewCookieOnPageVisibilityChange,
    updateBattingCageScriptPreviewCookiePersistedExperience,
    getPersistedExperienceFromBattingCagePreviewCookie,
  };
})();
